import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import {
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Stethoscope,
  Users,
  Loader2,
  RefreshCw,
  Pencil,
  LayoutGrid,
  ClipboardCheck,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useLocationUrlSync, useGlobalLocation } from '@/contexts/LocationContext';
import { formatDateNL, formatHoursHM, getISOWeek, isoWeekDates, navigateIsoWeek } from '@/lib/format';
import {
  StatusBadge, KpiTile, Sparkline, EditShiftDialog, WeekMatrix, MatrixLegend, UrenReviewPanel,
} from '@/components/uren';

const DAYS = [
  { dow: 1, short: 'Ma', long: 'Maandag' },
  { dow: 2, short: 'Di', long: 'Dinsdag' },
  { dow: 3, short: 'Wo', long: 'Woensdag' },
  { dow: 4, short: 'Do', long: 'Donderdag' },
  { dow: 5, short: 'Vr', long: 'Vrijdag' },
  { dow: 6, short: 'Za', long: 'Zaterdag' },
  { dow: 7, short: 'Zo', long: 'Zondag' },
];

const STATUS_META = {
  worked: { label: 'Gewerkt' },
  approved: { label: 'Goedgekeurd' },
  rejected: { label: 'Niet verschenen' },
};

const todayIso = () => new Date().toISOString().slice(0, 10);

function PickLocationHint() {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600"
      data-testid="pick-location-hint"
    >
      Kies een specifieke locatie voor deze weergave. De weekmatrix werkt ook met "Alle locaties".
    </div>
  );
}

export default function UrenOverzicht() {
  const { toast } = useToast();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();
  const { globalLocation } = useGlobalLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [locations, setLocations] = useState([]);
  const initial = getISOWeek();
  const [year, setYear] = useState(initial.year);
  const [week, setWeek] = useState(initial.week);
  const [weekData, setWeekData] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [selectedDow, setSelectedDow] = useState(1);
  const [activeTab, setActiveTab] = useState('week');
  const [reviewMode, setReviewMode] = useState(searchParams.get('filter') === 'review');
  const [reviewReloadKey, setReviewReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [dayLoading, setDayLoading] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [actionPending, setActionPending] = useState(null);
  const [editShift, setEditShift] = useState(null);
  const [editForm, setEditForm] = useState({ start_time: '', end_time: '', actual_clock_in: '', actual_clock_out: '' });
  const [editSaving, setEditSaving] = useState(false);

  const weekDates = useMemo(() => isoWeekDates(year, week), [year, week]);
  const selectedDateIso = useMemo(() => {
    const d = weekDates[selectedDow - 1];
    return d ? d.toISOString().slice(0, 10) : null;
  }, [weekDates, selectedDow]);

  // Keep reviewMode in sync with the ?filter query param (single source of truth).
  useEffect(() => {
    const isReview = searchParams.get('filter') === 'review';
    setReviewMode(isReview);
    if (isReview) setActiveTab('week');
  }, [searchParams]);

  // Bootstrap locations
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/locations');
        const eligible = (data || [])
          .filter((l) => l.has_roster !== false)
          .sort((a, b) => a.name.localeCompare(b.name, 'nl'));
        setLocations(eligible);
      } catch {
        toast({ title: 'Kon locaties niet laden', variant: 'destructive' });
      }
    })();
  }, [toast]);

  // Geen automatische terugval meer naar de eerste locatie: "Alle locaties"
  // (lege slug) is een geldige keuze — de matrix groepeert dan per locatie.

  const loadWeek = useCallback(async () => {
    if (!selectedSlug) { setWeekData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/uren/week', {
        params: { location_id: selectedSlug, year, week },
      });
      setWeekData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Kon weekoverzicht niet laden';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedSlug, year, week, toast]);

  const loadMatrix = useCallback(async () => {
    setMatrixLoading(true);
    try {
      const { data } = await api.get('/uren/week-matrix', {
        params: { location_id: selectedSlug || 'all', year, week },
      });
      setMatrixData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Kon weekmatrix niet laden';
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setMatrixLoading(false);
    }
  }, [selectedSlug, year, week, toast]);

  const loadDay = useCallback(async () => {
    if (!selectedSlug || !selectedDateIso) return;
    setDayLoading(true);
    try {
      const { data } = await api.get('/uren/day-status', {
        params: { location_id: selectedSlug, date_iso: selectedDateIso },
      });
      setDayData(data);
    } catch (e) {
      toast({ title: 'Kon dag niet laden', variant: 'destructive' });
    } finally {
      setDayLoading(false);
    }
  }, [selectedSlug, selectedDateIso, toast]);

  const loadTrend = useCallback(async () => {
    if (!selectedSlug) return;
    setTrendLoading(true);
    try {
      const { data } = await api.get('/uren/trend', {
        params: { location_id: selectedSlug, weeks_back: 8 },
      });
      setTrendData(data);
    } catch (e) {
      toast({ title: 'Kon trend niet laden', variant: 'destructive' });
    } finally {
      setTrendLoading(false);
    }
  }, [selectedSlug, toast]);

  useEffect(() => { loadWeek(); }, [loadWeek]);
  useEffect(() => {
    if (activeTab === 'week' && !reviewMode) loadMatrix();
  }, [activeTab, reviewMode, loadMatrix]);
  useEffect(() => { if (activeTab === 'per-dag') loadDay(); }, [activeTab, loadDay]);
  useEffect(() => { if (activeTab === 'trend') loadTrend(); }, [activeTab, loadTrend]);

  const shiftWeek = (delta) => {
    const { year: y, week: w } = navigateIsoWeek(year, week, delta);
    setWeek(w);
    setYear(y);
  };

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadWeek(),
      (activeTab === 'week' && !reviewMode) ? loadMatrix() : Promise.resolve(),
      activeTab === 'per-dag' ? loadDay() : Promise.resolve(),
    ]);
    setReviewReloadKey((k) => k + 1);
  }, [loadWeek, loadMatrix, loadDay, activeTab, reviewMode]);

  const handleApprove = async (assignmentIds, newStatus, reason) => {
    if (!assignmentIds || assignmentIds.length === 0) return;
    setActionPending(`${newStatus}-${assignmentIds[0]}`);
    try {
      const { data } = await api.post('/uren/approve', {
        assignment_ids: assignmentIds,
        new_status: newStatus,
        reason: reason || null,
      });
      if (data.blocked_locked_or_403 > 0) {
        toast({
          title: 'Deels geblokkeerd',
          description: `${data.updated} bijgewerkt, ${data.blocked_locked_or_403} geblokkeerd (locked periode of geen toegang).`,
        });
      } else {
        toast({ title: `${data.updated} bijgewerkt`, description: STATUS_META[newStatus]?.label });
      }
      await Promise.all([
        loadWeek(),
        loadMatrix(),
        activeTab === 'per-dag' ? loadDay() : Promise.resolve(),
      ]);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Bijwerken mislukt';
      toast({ title: typeof msg === 'string' ? msg : 'Bijwerken mislukt', variant: 'destructive' });
    } finally {
      setActionPending(null);
    }
  };

  const openEditFromMatrix = (cell, emp) => {
    setEditShift({
      ...cell,
      name: emp.name,
      personeelsnummer: emp.personeelsnummer,
      review_reason: cell.time_record_review_reason,
    });
    setEditForm({
      start_time: cell.actual_clock_in || cell.start_time || '',
      end_time: cell.actual_clock_out || cell.end_time || '',
      actual_clock_in: cell.actual_clock_in || '',
      actual_clock_out: cell.actual_clock_out || '',
    });
  };

  const openEditFromReview = (item) => {
    setEditShift({ ...item, time_record_needs_review: true });
    setEditForm({
      start_time: item.start_time || item.actual_clock_in || '',
      end_time: item.end_time || item.actual_clock_out || '',
      actual_clock_in: item.actual_clock_in || '',
      actual_clock_out: item.actual_clock_out || '',
    });
  };

  const openEditShift = (row) => {
    setEditShift({ ...row, review_reason: row.time_record_review_reason });
    setEditForm({
      start_time: row.actual_clock_in || row.start_time || '',
      end_time: row.actual_clock_out || row.end_time || '',
      actual_clock_in: row.actual_clock_in || '',
      actual_clock_out: row.actual_clock_out || '',
    });
  };

  const closeEditShift = () => {
    setEditShift(null);
    setEditForm({ start_time: '', end_time: '', actual_clock_in: '', actual_clock_out: '' });
  };

  const nlHHMMToUtcIso = (dateIso, hhmm) => {
    if (!dateIso || !/^\d{2}:\d{2}$/.test(hhmm || '')) return null;
    return new Date(`${dateIso}T${hhmm}:00`).toISOString();
  };

  const submitEditShift = async () => {
    if (!editShift) return;
    const { start_time, end_time, actual_clock_in, actual_clock_out } = editForm;
    if (!/^\d{2}:\d{2}$/.test(start_time) || !/^\d{2}:\d{2}$/.test(end_time)) {
      toast({ title: 'Ongeldige tijd', description: 'Gebruik HH:MM formaat', variant: 'destructive' });
      return;
    }
    if (end_time <= start_time) {
      toast({ title: 'Eindtijd moet na begintijd liggen', variant: 'destructive' });
      return;
    }
    setEditSaving(true);
    try {
      // 1) Werkelijke kloktijden corrigeren (indien gewijzigd en er een time_record is)
      if (editShift.time_record_id) {
        const dateIso = editShift.date || selectedDateIso;
        const payload = {};
        if (actual_clock_in && actual_clock_in !== (editShift.actual_clock_in || '')) {
          payload.clock_in = nlHHMMToUtcIso(dateIso, actual_clock_in);
        }
        if (actual_clock_out && actual_clock_out !== (editShift.actual_clock_out || '')) {
          payload.clock_out = nlHHMMToUtcIso(dateIso, actual_clock_out);
        }
        if (Object.keys(payload).length > 0) {
          await api.put(`/time-records/${editShift.time_record_id}`, payload);
        }
      }

      // 2) Geplande shift-tijden (bestaand gedrag, ETag-retry)
      if (editShift.assignment_id) {
        const assignmentId = editShift.assignment_id;
        let currentVersion = Number.isInteger(editShift.version) ? editShift.version : 0;
        let lastError = null;

        for (let attempt = 0; attempt < 3; attempt += 1) {
          const etag = `"${assignmentId}-${currentVersion}"`;
          try {
            await api.put(
              `/assignments/${assignmentId}`,
              { start_time, end_time },
              { headers: { 'If-Match': etag } },
            );
            lastError = null;
            break;
          } catch (e) {
            lastError = e;
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409 && detail && typeof detail === 'object' && Number.isInteger(detail.current_version)) {
              if (detail.current_version === currentVersion) break;
              currentVersion = detail.current_version;
              continue;
            }
            if (status === 409 || status === 412) break;
            throw e;
          }
        }
        if (lastError) throw lastError;
      }

      toast({ title: 'Bijgewerkt' });
      closeEditShift();
      await refreshAll();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 423) {
        msg = (typeof detail === 'string' ? detail : null) || 'Deze week is afgesloten — ontgrendel eerst via Verloning.';
      } else if (status === 409 || status === 412) {
        msg = 'Bewerken mislukt na meerdere pogingen. Ververs de tabel (Verversen-knop) en probeer opnieuw.';
      } else {
        msg = (typeof detail === 'string' ? detail : null) || 'Bijwerken mislukt';
      }
      toast({ title: 'Opslaan mislukt', description: msg, variant: 'destructive', duration: 8000 });
    } finally {
      setEditSaving(false);
    }
  };

  const resolveEditShiftReview = async () => {
    if (!editShift?.time_record_id) return;
    setEditSaving(true);
    try {
      await api.post(`/time-records/${editShift.time_record_id}/resolve-review`, {});
      toast({ title: 'Afgehandeld' });
      closeEditShift();
      await refreshAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({ title: 'Afhandelen mislukt', description: typeof detail === 'string' ? detail : undefined, variant: 'destructive' });
    } finally {
      setEditSaving(false);
    }
  };

  const toggleReview = () => {
    const next = !reviewMode;
    setActiveTab('week');
    // Only mutate the ?filter param; the sync effect derives reviewMode from it
    // (single source of truth, avoids toggle/effect double-set lag).
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next) p.set('filter', 'review'); else p.delete('filter');
      return p;
    }, { replace: true });
  };

  const totals = useMemo(() => {
    // Bij "Alle locaties" bestaat er geen per-locatie weekoverzicht: tel dan de
    // matrix (alle locaties samen) op.
    const list = weekData ? (weekData.per_employee || []) : (matrixData?.employees || []);
    if (list.length === 0) return { planned: 0, worked: 0, approved: 0, employees: 0, needsReview: 0 };
    return {
      planned: list.reduce((s, r) => s + (r.planned_hours || 0), 0),
      worked: list.reduce((s, r) => s + (r.worked_hours || 0), 0),
      approved: list.reduce((s, r) => s + (r.approved_hours || 0), 0),
      employees: list.length,
      needsReview: list.filter((r) => r.needs_review).length,
    };
  }, [weekData, matrixData]);

  const approvableInMatrix = useMemo(() => {
    if (!matrixData?.employees) return [];
    const ids = [];
    const t = todayIso();
    matrixData.employees.forEach((emp) => {
      Object.entries(emp.days || {}).forEach(([d, v]) => {
        (v.cells || []).forEach((c) => {
          if (c.status === 'planned' && d < t) ids.push(c.assignment_id);
        });
      });
    });
    return ids;
  }, [matrixData]);

  const matrixReviewCount = matrixData?.approvable_count || 0;

  const trendMax = useMemo(() => {
    if (!trendData?.series) return 1;
    return Math.max(...trendData.series.map((p) => p.total_hours || 0), 1);
  }, [trendData]);

  return (
    <div className="space-y-5" data-testid="page-uren-overzicht">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Uren Overzicht</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Weekmatrix, goedkeuring en trend van gewerkte uren per locatie.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="uren-location-select"
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm focus:border-brand-amber focus:outline-none"
          >
            {locations.length > 1 && <option value="">Alle locaties</option>}
            {locations.map((loc) => (
              <option key={loc.slug} value={loc.slug}>{loc.name}</option>
            ))}
          </select>
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white">
            <button
              type="button"
              data-testid="uren-week-prev"
              onClick={() => shiftWeek(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-l-md text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              aria-label="Vorige week"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="px-3 py-1.5 text-sm font-medium text-slate-700" data-testid="uren-week-label">
              Week {week} / {year}
            </div>
            <button
              type="button"
              data-testid="uren-week-next"
              onClick={() => shiftWeek(1)}
              className="flex h-9 w-9 items-center justify-center rounded-r-md text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              aria-label="Volgende week"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="uren-refresh"
            onClick={refreshAll}
            disabled={loading || matrixLoading}
            className="h-9"
          >
            <RefreshCw size={14} className={(loading || matrixLoading) ? 'animate-spin' : ''} />
            <span className="ml-1.5">Verversen</span>
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile icon={Users} label="Medewerkers" value={totals.employees} sublabel="met shifts deze week" accent="bg-brand-navy/10 text-brand-navy" />
        <KpiTile icon={Clock} label="Gepland" value={formatHoursHM(totals.planned)} sublabel="netto incl. pauze" />
        <KpiTile icon={CheckCircle2} label="Gewerkt" value={formatHoursHM(totals.worked)} sublabel="ingeklokt" accent="bg-emerald-100 text-emerald-700" />
        <KpiTile icon={CheckCircle2} label="Goedgekeurd" value={formatHoursHM(totals.approved)} sublabel="door manager" accent="bg-emerald-100 text-emerald-700" />
        <KpiTile
          icon={AlertCircle}
          label="Te reviewen"
          value={totals.needsReview}
          sublabel={totals.needsReview === 0 ? 'geen issues' : 'tijdregistratie incompleet'}
          accent={totals.needsReview > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto">
          <TabsTrigger value="week" data-testid="tab-week" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white">
            <LayoutGrid size={14} className="mr-1.5" /> Week
          </TabsTrigger>
          <TabsTrigger value="per-dag" data-testid="tab-per-dag" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white">
            <CalendarDays size={14} className="mr-1.5" /> Per dag
          </TabsTrigger>
          <TabsTrigger value="per-medewerker" data-testid="tab-per-medewerker" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white">
            <Users size={14} className="mr-1.5" /> Per medewerker
          </TabsTrigger>
          <TabsTrigger value="trend" data-testid="tab-trend" className="data-[state=active]:bg-brand-navy data-[state=active]:text-white">
            <TrendingUp size={14} className="mr-1.5" /> Trend
          </TabsTrigger>
        </TabsList>

        {/* WEEK (matrix / review) */}
        <TabsContent value="week" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant={reviewMode ? 'default' : 'outline'}
              size="sm"
              data-testid="toggle-review-filter"
              onClick={toggleReview}
              className={`h-9 ${reviewMode ? 'bg-amber-500 text-white hover:bg-amber-600' : ''}`}
            >
              <ClipboardCheck size={14} className="mr-1.5" />
              Alleen te beoordelen
              {matrixReviewCount > 0 && !reviewMode && (
                <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="review-count-badge">
                  {matrixReviewCount}
                </Badge>
              )}
            </Button>
            {!reviewMode && selectedSlug && approvableInMatrix.length > 0 && (
              <Button
                size="sm"
                data-testid={`bulk-approve-${selectedSlug}`}
                onClick={() => handleApprove(approvableInMatrix, 'approved', null)}
                disabled={!!actionPending}
                className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
              >
                {actionPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                Keur alles goed ({approvableInMatrix.length})
              </Button>
            )}
          </div>

          {reviewMode ? (
            <UrenReviewPanel
              globalLocation={globalLocation}
              onEditItem={openEditFromReview}
              onChanged={loadWeek}
              reloadKey={reviewReloadKey}
            />
          ) : matrixLoading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Laden…
            </div>
          ) : (
            <>
              <WeekMatrix
                weekDates={weekDates}
                employees={matrixData?.employees || []}
                groups={matrixData?.groups}
                todayIso={todayIso()}
                onApprove={handleApprove}
                onEditCell={openEditFromMatrix}
                actionPending={actionPending}
              />
              <MatrixLegend />
            </>
          )}
        </TabsContent>

        {/* PER DAG */}
        <TabsContent value="per-dag" className="mt-4 space-y-3">
          {!selectedSlug && <PickLocationHint />}
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => {
              const dateObj = weekDates[i];
              const dateIso = dateObj ? dateObj.toISOString().slice(0, 10) : null;
              const active = selectedDow === d.dow;
              return (
                <button
                  key={d.dow}
                  type="button"
                  data-testid={`day-picker-${d.short.toLowerCase()}`}
                  onClick={() => setSelectedDow(d.dow)}
                  className={`flex flex-col items-center justify-center rounded-md border px-3 py-2 text-xs transition-colors min-w-[64px] ${
                    active
                      ? 'border-brand-navy bg-brand-navy text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-brand-amber/50 hover:bg-slate-50'
                  }`}
                >
                  <span className="text-[10px] uppercase tracking-wider opacity-80">{d.short}</span>
                  <span className={`mt-0.5 text-sm font-semibold ${active ? 'text-white' : 'text-slate-900'}`}>
                    {dateIso ? dateIso.slice(8) : '—'}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="per-dag-table">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Calendar size={14} className="text-slate-400" />
                <span className="font-medium">
                  {DAYS[selectedDow - 1]?.long} {selectedDateIso ? formatDateNL(selectedDateIso) : ''}
                </span>
                {dayData && (
                  <Badge variant="secondary" className="ml-2 bg-slate-100">
                    {dayData.count} shifts
                  </Badge>
                )}
              </div>
            </div>

            {dayLoading ? (
              <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Laden…
              </div>
            ) : !dayData || dayData.rows.length === 0 ? (
              <div className="p-10 text-center">
                <CalendarDays size={32} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">Geen shifts op deze dag.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[260px]">Medewerker</TableHead>
                    <TableHead>Gepland</TableHead>
                    <TableHead>Werkelijk (ingeklokt)</TableHead>
                    <TableHead className="text-right">Netto uren</TableHead>
                    <TableHead className="text-center w-[120px]">Status</TableHead>
                    <TableHead className="text-right w-[320px]">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dayData.rows.map((row) => {
                    const isPending = actionPending && actionPending.endsWith(row.assignment_id);
                    const hasClockIn = !!row.actual_clock_in;
                    const hasClockOut = !!row.actual_clock_out;
                    return (
                      <TableRow key={row.assignment_id} data-testid={`day-row-${row.personeelsnummer}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-navy/10 text-[10px] font-semibold text-brand-navy">
                              {row.name?.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?'}
                            </div>
                            <div className="leading-tight">
                              <p className="text-sm font-medium text-slate-900">{row.name}</p>
                              <p className="text-[11px] text-slate-500">#{row.personeelsnummer} · {row.role || 'medewerker'}</p>
                            </div>
                            {row.is_hulpvraag && (
                              <Badge className="ml-1 bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">
                                Hulpvraag
                              </Badge>
                            )}
                            {row.source === 'ad_hoc_clock' && (
                              <Badge variant="outline" className="ml-1 border-slate-300 text-slate-500 text-[10px]">
                                Ad-hoc
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums text-slate-700" data-testid={`planned-${row.assignment_id}`}>
                          {row.start_time}–{row.end_time}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums" data-testid={`actual-${row.assignment_id}`}>
                          {hasClockIn ? (
                            <span className={hasClockOut ? 'text-emerald-700' : 'text-amber-700'}>
                              {row.actual_clock_in}–{hasClockOut ? row.actual_clock_out : 'nu'}
                              {!hasClockOut && (
                                <Badge variant="outline" className="ml-1.5 border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
                                  open
                                </Badge>
                              )}
                            </span>
                          ) : (
                            <span className="text-slate-400">— niet ingeklokt —</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          {formatHoursHM(row.duration_net)}
                          {row.break_minutes > 0 && (
                            <span className="ml-1 text-[10px] text-slate-400">-{row.break_minutes}m</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <StatusBadge status={row.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-shift-${row.assignment_id}`}
                              onClick={() => openEditShift(row)}
                              disabled={isPending}
                              className="h-7 px-2 text-[11px]"
                            >
                              <Pencil size={12} className="mr-1" /> Bewerk
                            </Button>
                            <Button
                              size="sm"
                              variant={row.status === 'approved' ? 'default' : 'outline'}
                              data-testid={`approve-worked-${row.assignment_id}`}
                              onClick={() => handleApprove([row.assignment_id], 'approved')}
                              disabled={isPending}
                              className={`h-7 px-2 text-[11px] ${row.status === 'approved' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : ''}`}
                            >
                              <CheckCircle2 size={12} className="mr-1" /> Gewerkt
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`approve-sick-${row.assignment_id}`}
                              onClick={() => handleApprove([row.assignment_id], 'approved', 'ziek')}
                              disabled={isPending}
                              className="h-7 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              <Stethoscope size={12} className="mr-1" /> Ziek
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`approve-rejected-${row.assignment_id}`}
                              onClick={() => handleApprove([row.assignment_id], 'rejected', 'niet verschenen')}
                              disabled={isPending}
                              className="h-7 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                            >
                              <XCircle size={12} className="mr-1" /> Niet versch.
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* PER MEDEWERKER */}
        <TabsContent value="per-medewerker" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="per-medewerker-table">
            {loading ? (
              <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Laden…
              </div>
            ) : !weekData || (weekData.per_employee || []).length === 0 ? (
              <div className="p-10 text-center">
                <Calendar size={32} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">Geen shifts voor deze week.</p>
                <p className="mt-1 text-xs text-slate-400">Plan eerst shifts via Roosterplanner.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[280px]">Medewerker</TableHead>
                    <TableHead className="text-right">Contract</TableHead>
                    <TableHead className="text-right">Gepland</TableHead>
                    <TableHead className="text-right">Gewerkt</TableHead>
                    <TableHead className="text-right">Goedgekeurd</TableHead>
                    <TableHead className="text-right">Niet versch.</TableHead>
                    <TableHead className="text-right">Ziek/Vakantie</TableHead>
                    <TableHead className="text-right">Verschil</TableHead>
                    <TableHead className="text-center w-[110px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(weekData.per_employee || []).map((row) => {
                    const diff = row.verschil || 0;
                    const diffColor = diff > 0.05 ? 'text-emerald-600' : diff < -0.05 ? 'text-rose-600' : 'text-slate-500';
                    return (
                      <TableRow key={row.id} data-testid={`uren-row-${row.personeelsnummer}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-navy/10 text-[10px] font-semibold text-brand-navy">
                              {row.name?.split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase() || '?'}
                            </div>
                            <div className="leading-tight">
                              <p className="text-sm font-medium text-slate-900">{row.name}</p>
                              <p className="text-[11px] text-slate-500">
                                #{row.personeelsnummer} · {row.role || 'medewerker'}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-slate-600">
                          {formatHoursHM(row.contract_hours)}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{formatHoursHM(row.planned_hours)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-emerald-700">
                          {formatHoursHM(row.worked_hours)}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-emerald-700">
                          {formatHoursHM(row.approved_hours)}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-rose-600">
                          {formatHoursHM(row.rejected_hours)}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-slate-600">
                          {formatHoursHM((row.ziekte_hours || 0) + (row.vakantie_hours || 0))}
                        </TableCell>
                        <TableCell className={`text-right text-sm font-medium tabular-nums ${diffColor}`}>
                          {diff > 0 ? '+' : ''}{formatHoursHM(diff)}
                        </TableCell>
                        <TableCell className="text-center">
                          {row.needs_review ? (
                            <Badge
                              variant="outline"
                              data-testid={`needs-review-${row.personeelsnummer}`}
                              className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-50"
                            >
                              <AlertCircle size={11} className="mr-1" /> Review
                            </Badge>
                          ) : (
                            <span className="text-[11px] text-slate-400">OK</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* TREND */}
        <TabsContent value="trend" className="mt-4 space-y-3">
          {!selectedSlug && <PickLocationHint />}
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="trend-card">
            {trendLoading ? (
              <div className="p-6 text-center text-slate-500 flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Laden…
              </div>
            ) : !trendData?.series ? (
              <p className="text-sm text-slate-500">Geen trenddata beschikbaar.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Trend laatste 8 weken</h3>
                    <p className="text-[11px] text-slate-500">Totaal gewerkte uren per ISO-week op deze locatie.</p>
                  </div>
                  <div className="flex items-center gap-2 text-brand-navy">
                    <Sparkline
                      data={trendData.series.map((p) => p.total_hours || 0)}
                      max={trendMax}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Week</TableHead>
                        <TableHead className="text-right">Totaal uren</TableHead>
                        <TableHead className="text-right">Shifts</TableHead>
                        <TableHead className="text-right">Unieke medewerkers</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trendData.series.map((p) => (
                        <TableRow key={`${p.year}-${p.week}`} data-testid={`trend-row-${p.year}-${p.week}`}>
                          <TableCell className="text-sm font-medium text-slate-900">
                            Week {p.week} / {p.year}
                          </TableCell>
                          <TableCell className="text-right text-sm tabular-nums">{formatHoursHM(p.total_hours)}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums text-slate-600">{p.shifts}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums text-slate-600">{p.unique_employees}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit shift dialog (shared) */}
      <EditShiftDialog
        editShift={editShift}
        selectedDateIso={editShift?.date || selectedDateIso}
        editForm={editForm}
        setEditForm={setEditForm}
        editSaving={editSaving}
        onClose={closeEditShift}
        onSubmit={submitEditShift}
        onResolve={resolveEditShiftReview}
      />
    </div>
  );
}
